import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { Copy, Plus, RefreshCw, Store, Trash2 } from "lucide-react";
import { ConfirmDeleteModal } from "./ConfirmDeleteModal";

const TRADES = [
  { value: "boulangerie", label: "Boulangerie" },
  { value: "boucherie", label: "Boucherie" },
  { value: "restaurant", label: "Restaurant" },
  { value: "commerce", label: "Commerce" },
  { value: "autre", label: "Autre" },
];

const NO_LIC = "__none__";

const EMPTY = { nom: "", trade: "commerce", ville: "", code_postal: "", adresse: "", email_contact: "", telephone: "", licencie_id: NO_LIC };

export function MerchantsPanel({ canAssignLicencie = false, canDelete = false }) {
  const [items, setItems] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [licencies, setLicencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [inviteFor, setInviteFor] = useState(null);
  const [inviteForm, setInviteForm] = useState({ email: "", prenom: "", nom: "", telephone: "" });
  const [inviteDevLink, setInviteDevLink] = useState(null);
  const [inviteError, setInviteError] = useState(null);
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: ms }, { data: invs }] = await Promise.all([
        api.get("/merchants"),
        api.get("/invitations").catch(() => ({ data: [] })),
      ]);
      setItems(ms || []);
      setInvitations(invs || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLicencies = useCallback(async () => {
    if (!canAssignLicencie) return;
    try {
      const { data } = await api.get("/invitations");
      // Hack Phase 1: list licenciés via invitations 'used' (active). In Phase 2 we'll have a /users endpoint.
      // For now derive from /invitations (used=true means accepted → user exists).
      // But we also need licencié IDs. Simpler: hit a fresh endpoint if available, else parse from /api/auth/me responses. Since none exists, we just list "used" invitations as proxy.
      // To keep behaviour correct: leave empty for now; admin can leave Siège or type ID manually. We'll improve in Phase 2.
      void data;
    } catch {/* ignore */}
  }, [canAssignLicencie]);

  useEffect(() => { reload(); loadLicencies(); }, [reload, loadLicencies]);

  const openCreate = () => { setEditingId(null); setForm(EMPTY); setError(null); setOpen(true); };
  const openEdit = (m) => {
    setEditingId(m.id);
    setForm({
      nom: m.nom || "",
      trade: m.trade || "commerce",
      ville: m.ville || "",
      code_postal: m.code_postal || "",
      adresse: m.adresse || "",
      email_contact: m.email_contact || "",
      telephone: m.telephone || "",
      licencie_id: m.licencie_id || NO_LIC,
    });
    setError(null);
    setOpen(true);
  };

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const body = {
        nom: form.nom.trim(),
        trade: form.trade,
        ville: form.ville.trim(),
        code_postal: form.code_postal.trim(),
        adresse: form.adresse.trim() || undefined,
        email_contact: form.email_contact.trim() || undefined,
        telephone: form.telephone.trim() || undefined,
      };
      if (canAssignLicencie) {
        body.licencie_id = form.licencie_id === NO_LIC ? null : form.licencie_id;
      }
      if (editingId) {
        await api.patch(`/merchants/${editingId}`, body);
        toast.success("Commerçant mis à jour");
      } else {
        await api.post("/merchants", body);
        toast.success("Commerçant créé");
      }
      setOpen(false);
      reload();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/merchants/${toDelete.id}`);
      toast.success("Commerçant supprimé (compte utilisateur supprimé également)");
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
      throw err;
    }
  };

  const openInvite = (m) => {
    setInviteFor(m);
    // Best-effort split of merchant name: first word → prenom, rest → nom
    const parts = (m.nom || "").trim().split(/\s+/);
    const prenom = parts.length > 1 ? parts[0] : "";
    const nom = parts.length > 1 ? parts.slice(1).join(" ") : (m.nom || "");
    setInviteForm({
      email: m.email_contact || "",
      telephone: m.telephone || "",
      prenom,
      nom,
    });
    setInviteDevLink(null);
    setInviteError(null);
  };
  const submitInvite = async (e) => {
    e.preventDefault();
    setInviteError(null);
    setInviteSubmitting(true);
    try {
      const { data } = await api.post("/invitations", {
        email: inviteForm.email.trim(),
        prenom: inviteForm.prenom.trim(),
        nom: inviteForm.nom.trim(),
        role: "commercant",
        merchant_id: inviteFor.id,
        telephone: inviteForm.telephone.trim() || undefined,
      });
      toast.success("Invitation commerçant envoyée");
      setInviteDevLink(data.dev_link || null);
      reload();
      if (!data.dev_link) setInviteFor(null);
    } catch (err) {
      setInviteError(formatApiError(err));
    } finally {
      setInviteSubmitting(false);
    }
  };

  const accountStateFor = (m) => {
    if (m.account_user_id) return { kind: "active" };
    const pending = invitations.find(
      (i) => i.role === "commercant" && i.merchant_id === m.id && i.status === "pending"
    );
    if (pending) return { kind: "pending", invitation: pending };
    return { kind: "none" };
  };

  return (
    <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-[0_8px_24px_rgba(57,125,255,0.06)] p-6 sm:p-7" data-testid="merchants-panel">
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-2xl font-bold text-[#191D1E]">Commerçants</h2>
          <p className="text-sm text-[#939BA0] mt-0.5">Réseau partenaire pour le cashback 30 €.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={reload} data-testid="merchants-refresh" className="rounded-xl border-[#E5E7EB] hover:bg-[#F5F6F9]">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Actualiser
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreate} data-testid="merchants-add" className="bg-[#F9C80E] hover:bg-[#DDB10A] text-[#191D1E] font-semibold rounded-xl shadow-[0_4px_14px_rgba(249,200,14,0.32)]">
                <Plus className="w-4 h-4 mr-2" /> Ajouter
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="merchant-dialog">
              <DialogHeader>
                <DialogTitle className="font-display text-xl">{editingId ? "Modifier le commerçant" : "Nouveau commerçant"}</DialogTitle>
                <DialogDescription>Renseignez les informations du point de vente partenaire.</DialogDescription>
              </DialogHeader>
              <form onSubmit={onSubmit} className="space-y-3" data-testid="merchant-form">
                <div className="space-y-1.5"><Label>Nom</Label><Input data-testid="merchant-nom" value={form.nom} onChange={onChange("nom")} required /></div>
                <div className="space-y-1.5">
                  <Label>Activité</Label>
                  <Select value={form.trade} onValueChange={(v) => setForm((f) => ({ ...f, trade: v }))}>
                    <SelectTrigger data-testid="merchant-trade"><SelectValue /></SelectTrigger>
                    <SelectContent>{TRADES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Ville</Label><Input data-testid="merchant-ville" value={form.ville} onChange={onChange("ville")} required /></div>
                  <div className="space-y-1.5"><Label>Code postal</Label><Input data-testid="merchant-cp" value={form.code_postal} onChange={onChange("code_postal")} required /></div>
                </div>
                <div className="space-y-1.5"><Label>Adresse (optionnel)</Label><Input data-testid="merchant-adresse" value={form.adresse} onChange={onChange("adresse")} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Email contact</Label><Input data-testid="merchant-email" type="email" value={form.email_contact} onChange={onChange("email_contact")} /></div>
                  <div className="space-y-1.5"><Label>Téléphone</Label><Input data-testid="merchant-tel" value={form.telephone} onChange={onChange("telephone")} /></div>
                </div>
                {canAssignLicencie && (
                  <div className="space-y-1.5">
                    <Label>Licencié rattaché</Label>
                    <Select value={form.licencie_id} onValueChange={(v) => setForm((f) => ({ ...f, licencie_id: v }))}>
                      <SelectTrigger data-testid="merchant-licencie"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NO_LIC}>Siège (sans licencié)</SelectItem>
                        {licencies.map((l) => <SelectItem key={l.id} value={l.id}>{l.prenom} {l.nom} — {l.zone}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-[#939BA0]">Pour rattacher à un licencié, utilisez la modification après création (Phase 2 : sélecteur enrichi).</p>
                  </div>
                )}
                {error && <div data-testid="merchant-error" className="px-3 py-2 rounded-lg bg-[#FEF2F2] text-[#B91C1C] text-sm border border-[#FECACA]">{error}</div>}
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-xl">Annuler</Button>
                  <Button type="submit" data-testid="merchant-submit" disabled={submitting} className="bg-[#F9C80E] hover:bg-[#DDB10A] text-[#191D1E] font-semibold rounded-xl">{submitting ? "Enregistrement..." : "Enregistrer"}</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {items.length === 0 && !loading && (
        <div className="py-10 text-center text-sm text-[#939BA0]" data-testid="merchants-empty">
          <Store className="w-8 h-8 mx-auto mb-3 text-[#BFC6CC]" />
          Aucun commerçant pour l'instant.<br />Cliquez sur « Ajouter » pour en créer un.
        </div>
      )}

      {items.length > 0 && (
        <div className="overflow-x-auto -mx-2">
          <table className="w-full text-sm" data-testid="merchants-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-[#939BA0] border-b border-[#E5E7EB]">
                <th className="px-2 py-2">Nom</th>
                <th className="px-2 py-2">Activité</th>
                <th className="px-2 py-2">Ville</th>
                <th className="px-2 py-2">Zone</th>
                <th className="px-2 py-2">Contact</th>
                <th className="px-2 py-2">Compte</th>
                <th className="px-2 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((m) => (
                <tr key={m.id} className="border-b border-[#EEF0F4] last:border-0" data-testid={`merchant-row-${m.id}`}>
                  <td className="px-2 py-3">
                    <div className="font-semibold text-[#191D1E]">{m.nom}</div>
                    {m.adresse && <div className="text-xs text-[#939BA0]">{m.adresse}</div>}
                  </td>
                  <td className="px-2 py-3"><Badge className="bg-[#EEF4FB] text-[#397DFF] border border-[#D7E5F4]">{m.trade_label}</Badge></td>
                  <td className="px-2 py-3 text-[#191D1E]">{m.ville} <span className="text-xs text-[#939BA0]">· {m.code_postal}</span></td>
                  <td className="px-2 py-3 text-[#687175]">{m.zone || "—"}</td>
                  <td className="px-2 py-3 text-[#687175]">
                    {m.email_contact && <div className="text-xs">{m.email_contact}</div>}
                    {m.telephone && <div className="text-xs">{m.telephone}</div>}
                  </td>
                  <td className="px-2 py-3">
                    {(() => {
                      const s = accountStateFor(m);
                      if (s.kind === "active") return <Badge data-testid={`merchant-account-active-${m.id}`} className="bg-[#E8F7EE] text-[#1F7A3D] border border-[#BFE5CC]">Compte actif</Badge>;
                      if (s.kind === "pending") return <Badge data-testid={`merchant-account-pending-${m.id}`} className="bg-[#FEF6C3] text-[#7A4A00] border border-[#F4E48C]">Invitation envoyée</Badge>;
                      return <Button size="sm" variant="outline" onClick={() => openInvite(m)} data-testid={`merchant-invite-${m.id}`} className="rounded-lg h-8 text-xs">Inviter</Button>;
                    })()}
                  </td>
                  <td className="px-2 py-3 text-right whitespace-nowrap">
                    <Button variant="outline" size="sm" onClick={() => openEdit(m)} data-testid={`merchant-edit-${m.id}`} className="rounded-lg mr-2">Modifier</Button>
                    {canDelete && (
                      <Button variant="outline" size="sm" onClick={() => setToDelete(m)} data-testid={`merchant-delete-${m.id}`} className="rounded-lg text-[#B91C1C] border-[#FECACA] hover:bg-[#FEF2F2]">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!inviteFor} onOpenChange={(v) => !v && setInviteFor(null)}>
        <DialogContent data-testid="invite-merchant-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Inviter le commerçant</DialogTitle>
            <DialogDescription>
              {inviteFor && <>Pour <strong>{inviteFor.nom}</strong> · {inviteFor.ville}. Un email d'activation lui sera envoyé.</>}
            </DialogDescription>
          </DialogHeader>
          {!inviteDevLink ? (
            <form onSubmit={submitInvite} className="space-y-3" data-testid="invite-merchant-form">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Prénom</Label><Input data-testid="invm-prenom" value={inviteForm.prenom} onChange={(e) => setInviteForm((f) => ({ ...f, prenom: e.target.value }))} required /></div>
                <div className="space-y-1.5"><Label>Nom</Label><Input data-testid="invm-nom" value={inviteForm.nom} onChange={(e) => setInviteForm((f) => ({ ...f, nom: e.target.value }))} required /></div>
              </div>
              <div className="space-y-1.5"><Label>Email</Label><Input type="email" data-testid="invm-email" value={inviteForm.email} onChange={(e) => setInviteForm((f) => ({ ...f, email: e.target.value }))} required /></div>
              <div className="space-y-1.5"><Label>Téléphone (optionnel)</Label><Input data-testid="invm-tel" value={inviteForm.telephone} onChange={(e) => setInviteForm((f) => ({ ...f, telephone: e.target.value }))} /></div>
              {inviteError && <div data-testid="invm-error" className="px-3 py-2 rounded-lg bg-[#FEF2F2] text-[#B91C1C] text-sm border border-[#FECACA]">{inviteError}</div>}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setInviteFor(null)} className="rounded-xl">Annuler</Button>
                <Button type="submit" disabled={inviteSubmitting} data-testid="invm-submit" className="bg-[#F9C80E] hover:bg-[#DDB10A] text-[#191D1E] font-semibold rounded-xl">{inviteSubmitting ? "Envoi…" : "Envoyer l'invitation"}</Button>
              </DialogFooter>
            </form>
          ) : (
            <div className="space-y-3" data-testid="invm-devlink">
              <div className="rounded-xl bg-[#FEF6C3] border border-[#F4E48C] p-4">
                <div className="text-[#7A4A00] text-xs uppercase tracking-wider font-semibold mb-2">Mode dev — lien à transmettre</div>
                <div className="text-xs text-[#191D1E] break-all bg-white rounded-lg p-2 border border-[#F4E48C]">{inviteDevLink}</div>
                <Button type="button" onClick={() => { navigator.clipboard.writeText(inviteDevLink); toast.success("Lien copié"); }} data-testid="invm-copy" className="mt-2 h-9 bg-[#397DFF] hover:bg-[#2860D8] text-white text-sm rounded-lg"><Copy className="w-3.5 h-3.5 mr-1.5" /> Copier</Button>
              </div>
              <DialogFooter>
                <Button type="button" onClick={() => setInviteFor(null)} className="rounded-xl bg-[#397DFF] hover:bg-[#2860D8] text-white">Fermer</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDeleteModal
        open={!!toDelete}
        onOpenChange={(v) => !v && setToDelete(null)}
        entityType="commerçant"
        entityLabel={toDelete?.email_contact || toDelete?.nom || ""}
        description={`Vous êtes sur le point de supprimer le commerçant "${toDelete?.nom || ""}".`}
        consequences={[
          "Le commerçant et son compte utilisateur sont supprimés définitivement.",
          "Les leads/clients rattachés restent en base avec leur merchant_id intact (historique préservé).",
          "Le commerçant ne pourra plus se connecter à la plateforme.",
        ]}
        onConfirm={onDelete}
      />
    </div>
  );
}

export default MerchantsPanel;
